import type { AuthUser } from "./types/profile";
import { Suspense, lazy, useEffect } from "react";
import { useAppSelector as useSelector } from "./store/hooks";
import { setStyles } from "./globalFunctions/setStyles";
import { Navigate, Route, Routes, useParams, Outlet, useLocation } from "react-router-dom";
import { useAuthState } from "react-firebase-hooks/auth";
import Layout from "./components/layouts/Layout";
import { useFirebase } from "./context/FirebaseContext";
import Loader from "./components/InsertElements/Loader";
import { routeLoaders } from "./utility/routeLoaders";
import { lazyWithChunkRecovery } from "./utility/chunkLoadRecovery";
import { useUserBackgrounds } from "./context/UserBackgroundContext";
import { getDefaultMetaDescription, usePageMeta } from "./hooks/usePageMeta";
import defaultBackground from "./images/background.webp";
import LanguageBoundary from "./i18n/LanguageBoundary";
import { rememberPostLoginRedirect } from "./utility/postLoginRedirect";
import { useInterfacePreferences } from "./hooks/useInterfacePreferences";
import {
  isFilmPagePath,
  resolveLayoutUserId
} from "./utility/layoutUserScope";
import { useI18n } from "./i18n/useI18n";
import ProfileAccessBoundary from "./components/ProfileAccessBoundary";
import {
  getProfileAppearanceUserId,
  resolveAppBackgroundForProfileRoute
} from "./utility/profileAppearance";

const Estimates = lazy(routeLoaders.estimates);
const MyResults = lazy(routeLoaders.results);
const Films = lazy(routeLoaders.films);
const CuratedLists = lazy(routeLoaders.collections);
const GameModes = lazy(routeLoaders.game);
const FilmPage = lazy(routeLoaders.filmPage);
const Friends = lazy(routeLoaders.friends);
const Profile = lazy(routeLoaders.profile);
const SearchResults = lazy(lazyWithChunkRecovery(() => import("./components/Tabs/SearchResults")));
const Achievements = lazy(routeLoaders.achievements);
const ActivityHub = lazy(lazyWithChunkRecovery(() => import("./components/Tabs/ActivityHub")));
const DataSources = lazy(lazyWithChunkRecovery(() => import("./components/Tabs/DataSources")));
const ImportRatings = lazy(lazyWithChunkRecovery(() => import("./components/Tabs/ImportRatings")));
const CastAndCrewPage = lazy(lazyWithChunkRecovery(() =>
  import("./components/InsertElements/CastAndCrewPage"))
);
const Login = lazy(lazyWithChunkRecovery(() => import("./components/Login")));
const Person = lazy(lazyWithChunkRecovery(() => import("./components/Person")));
const User = lazy(lazyWithChunkRecovery(() => import("./components/User")));
const FirestoreSecuritySmoke = process.env.REACT_APP_FIREBASE_SECURITY_SMOKE === "true"
  ? lazy(() => import("./components/security/FirestoreSecuritySmoke"))
  : null;

const RouteLoader = () => {
  const { t } = useI18n();
  return (
    <div className="page-loader" role="status" aria-live="polite">
      <div className="page-loader__content">
        <Loader />
        <span>{t("Загрузка страницы...")}</span>
      </div>
    </div>
  );
};

const META_DESCRIPTIONS: Record<"ru" | "en", Record<string, string | undefined>> = {
  ru: {
    estimates: "Изучайте подробную статистику по фильмам, жанрам, странам, десятилетиям и оценкам и сравнивайте свои привычки с друзьями.",
    results: "Подводите итоги месяца и года: активность, оценки, география, достижения и прогресс на CineParagon.",
    collections: "Находите, что посмотреть дальше, с персональными рекомендациями и тематическими подборками.",
    achievements: "Просматривайте открытые кинодостижения и архив долгосрочного прогресса.",
    game: "Проходите кинопутешествия, карты недели и одиночные экспедиции на CineParagon.",
    activity: "Ведите личный кинодневник и возвращайтесь к истории своих просмотров."
  },
  en: {
    estimates: "Explore detailed movie statistics, genres, countries, decades, ratings and compare your viewing habits with friends.",
    results: "Review your monthly and yearly movie results, activity, ratings, geography, achievements and progress on CineParagon.",
    collections: "Find what to watch next with personal recommendations and thematic movie collections.",
    achievements: "Browse your unlocked movie achievements and long-term progress archive.",
    game: "Complete movie journeys, weekly cards and solo dungeons on CineParagon.",
    activity: "Keep a personal movie diary and relive your cinema journey."
  }
};

const ROUTE_TITLES: Record<"ru" | "en", Record<string, string | undefined>> = {
  ru: {
    profile: "Паспорт киномана | CineParagon",
    estimates: "Киностатистика | CineParagon",
    results: "Мои киноитоги | CineParagon",
    friends: "Друзья | CineParagon",
    films: "Мои списки фильмов | CineParagon",
    collections: "Что посмотреть | CineParagon",
    achievements: "Достижения | CineParagon",
    game: "Путешествия | CineParagon",
    activity: "Кинодневник | CineParagon",
    notifications: "События | CineParagon",
    search: "Поиск фильмов | CineParagon",
    sources: "Источники данных | CineParagon",
    import: "Импорт оценок | CineParagon",
    cast: "Съёмочная группа | CineParagon"
  },
  en: {
    profile: "Movie Passport | CineParagon",
    estimates: "Movie Statistics | CineParagon",
    results: "My Movie Results | CineParagon",
    friends: "Friends | CineParagon",
    films: "My Movie Lists | CineParagon",
    collections: "What to Watch | CineParagon",
    achievements: "Achievements | CineParagon",
    game: "Journeys | CineParagon",
    activity: "Movie Diary | CineParagon",
    notifications: "Notifications | CineParagon",
    search: "Movie Search | CineParagon",
    sources: "Data Sources | CineParagon",
    import: "Import Ratings | CineParagon",
    cast: "Cast & Crew | CineParagon"
  }
};

const getRouteSection = (pathname = "") => {
  const parts = pathname.split("/").filter(Boolean);
  if (parts[0] === "film-page" && parts.includes("cast-and-crew-list")) return "cast";
  if (parts[0] === "film-page") return "film";
  if (parts[0] === "person") return "person";
  if (parts[0] === "user") return "user";
  if (parts[0] === "profile") return "profile";
  if (parts[0] === "login" || parts.length === 0) return "home";
  return parts[1] || "home";
};

const getPageSurfaceMode = (pathname = "") => {
  const section = getRouteSection(pathname);
  return ["profile", "achievements", "game"].includes(section)
    ? "cinematic"
    : "readable";
};

const PageMetaManager = () => {
  const location = useLocation();
  const { language } = useI18n();
  const section = getRouteSection(location.pathname);
  const normalizedLanguage = language === "en" ? "en" : "ru";
  const title = section === "film"
    ? null
    : ROUTE_TITLES[normalizedLanguage][section] || "CineParagon";
  const description = META_DESCRIPTIONS[normalizedLanguage][section] ||
    getDefaultMetaDescription(normalizedLanguage);

  usePageMeta(title, description);
  return null;
};

const ShareProfileEntry = ({ user }: { user?: AuthUser | null }) => {
  const { shareUserId } = useParams();
  const targetPath = shareUserId ? `/profile/${encodeURIComponent(shareUserId)}` : "/";

  useEffect(() => {
    if (!user && shareUserId) rememberPostLoginRedirect(targetPath);
  }, [shareUserId, targetPath, user]);

  if (!shareUserId) return <Navigate to="/" replace />;

  return user
    ? <Navigate to={targetPath} replace />
    : <Navigate to="/login" replace state={{ redirectTo: targetPath }} />;
};

const SharePersonalTopEntry = ({ user }: { user?: AuthUser | null }) => {
  const { shareUserId } = useParams();
  const targetPath = shareUserId
    ? `/${encodeURIComponent(shareUserId)}/films?list=topMovies`
    : "/";

  useEffect(() => {
    if (!user && shareUserId) rememberPostLoginRedirect(targetPath);
  }, [shareUserId, targetPath, user]);

  if (!shareUserId) return <Navigate to="/" replace />;
  return user
    ? <Navigate to={targetPath} replace />
    : <Navigate to="/login" replace state={{ redirectTo: targetPath }} />;
};

const ProfilePathAlias = ({ user }: { user?: AuthUser | null }) => {
  const { profileUserId } = useParams();
  const targetPath = profileUserId ? `/${encodeURIComponent(profileUserId)}/profile` : "/";

  useEffect(() => {
    if (!user && profileUserId) rememberPostLoginRedirect(targetPath);
  }, [profileUserId, targetPath, user]);

  if (!profileUserId) return <Navigate to="/" replace />;
  return user
    ? <Navigate to={targetPath} replace />
    : <Navigate to="/login" replace state={{ redirectTo: targetPath }} />;
};

const NotificationsRedirect = () => {
  const { userId } = useParams();
  const activityPath = userId
    ? `/${encodeURIComponent(userId)}/activity?tab=events`
    : "/";

  return <Navigate to={activityPath} replace />;
};

const ComparisonProfileAccess = ({ user }: { user?: AuthUser | null }) => {
  const { id } = useParams();
  if (!id) return <Navigate to="/" replace />;
  return (
    <ProfileAccessBoundary ownerId={id} viewer={user}>
      {() => <User />}
    </ProfileAccessBoundary>
  );
};

const AppLayoutWrapper = () => {
  const { t } = useI18n();
  const { auth } = useFirebase();
  const [user, loading] = useAuthState(auth);
  const location = useLocation();

  const { userId: urlUserId } = useParams();
  const stateUserId: unknown = location.state?.userId;
  const isFilmRoute = isFilmPagePath(location.pathname);
  if (urlUserId) {
    try { sessionStorage.setItem("contextUserId", urlUserId); } catch {}
  } else if (typeof stateUserId === "string" && stateUserId && !isFilmRoute) {
    try { sessionStorage.setItem("contextUserId", stateUserId); } catch {}
  }

  let storedUserId: string | null = null;
  try { storedUserId = sessionStorage.getItem("contextUserId"); } catch {}
  // На странице фильма оценка и списки всегда принадлежат
  // вошедшему пользователю, даже если он пришёл из профиля друга.
  const contextUserId = resolveLayoutUserId({
    pathname: location.pathname,
    authUserId: user?.uid,
    urlUserId,
    stateUserId: typeof stateUserId === "string" ? stateUserId : undefined,
    storedUserId
  });

  if (loading) {
    return (
      <div className="page-loader" role="status" aria-live="polite">
        <div className="page-loader__content">
          <Loader />
          <span>{t("Загрузка аутентификации...")}</span>
        </div>
      </div>
    );
  }

  if (!contextUserId) {
    return null;
  }

  return (
    <ProfileAccessBoundary ownerId={contextUserId} viewer={user}>
      {(profileAccess) => (
        <Layout key={contextUserId} userId={contextUserId} profileAccess={profileAccess}>
          <Outlet />
        </Layout>
      )}
    </ProfileAccessBoundary>
  );
};

const AppRouter = () => {
  const { auth } = useFirebase();
  const [user] = useAuthState(auth);
  const location = useLocation();
  const themeColor = useSelector((state) => state.settings.themeColor);
  const { motionEffects } = useInterfacePreferences();
  const {
    appBackgroundUrl: ownAppBackgroundUrl,
    viewedProfileAppearance
  } = useUserBackgrounds();
  const appearanceUserId = getProfileAppearanceUserId(location.pathname, user?.uid);
  // Layout activates this scope only after ProfileAccessBoundary grants
  // access. Until the target profile is loaded, a foreign page uses the
  // neutral default instead of leaking the signed-in user's personal image.
  const appBackgroundUrl = resolveAppBackgroundForProfileRoute({
    appearanceUserId,
    authUserId: user?.uid,
    ownBackgroundUrl: ownAppBackgroundUrl,
    viewedProfileAppearance,
    fallbackUrl: defaultBackground
  });
  const pageSurfaceMode = getPageSurfaceMode(location.pathname);
  const appBackgroundStyle = appBackgroundUrl
    ? { backgroundImage: `url(${appBackgroundUrl})` }
    : { backgroundImage: "none" };

  useEffect(() => {
    setStyles(themeColor);
  }, [themeColor]);

  return (
    <div
      className={[
        "app-router",
        "app-background",
        `page-${pageSurfaceMode}`,
        themeColor,
        !motionEffects ? "motion-effects-disabled" : ""
      ].filter(Boolean).join(" ")}
      style={appBackgroundStyle}
    >
      <div className="dimmer-layer" />
      <PageMetaManager />

      <LanguageBoundary>
        <Suspense fallback={<RouteLoader />}>
          <Routes>
            <Route path="/share/profile/:shareUserId" element={<ShareProfileEntry user={user} />} />
            <Route path="/share/top/:shareUserId" element={<SharePersonalTopEntry user={user} />} />
            <Route path="/profile/:profileUserId" element={<ProfilePathAlias user={user} />} />
            <Route path="login" element={<Login />} />
            <Route path="demo" element={<Login />} />
            <Route path="/" element={<Login />} />
            {FirestoreSecuritySmoke && (
              <Route
                path="/__firestore-security-smoke"
                element={user
                  ? <FirestoreSecuritySmoke />
                  : <Navigate
                      to="/login"
                      replace
                      state={{ redirectTo: "/__firestore-security-smoke" }}
                    />}
              />
            )}

            {user && (
              <Route element={<AppLayoutWrapper />}>
                <Route path="/:userId/profile" element={<Profile />} />
                <Route path="/:userId/estimates" element={<Estimates />} />
                <Route path="/:userId/estimates/:statisticsSection" element={<Estimates />} />
                <Route path="/:userId/results" element={<MyResults />} />
                <Route path="/:userId/films" element={<Films />} />
                <Route path="/:userId/collections" element={<CuratedLists />} />
                <Route path="/:userId/game" element={<GameModes />} />
                <Route path="/:userId/search" element={<SearchResults />} />
                <Route path="/:userId/friends" element={<Friends />} />
                <Route path="/:userId/achievements" element={<Achievements />} />
                <Route path="/:userId/activity" element={<ActivityHub />} />
                <Route path="/:userId/notifications" element={<NotificationsRedirect />} />
                <Route path="/:userId/sources" element={<DataSources />} />
                <Route path="/:userId/import" element={<ImportRatings />} />
                <Route path="/film-page/:mediaType/:id" element={<FilmPage />} />
                <Route path="/film-page/:id" element={<FilmPage />} />
                <Route
                  path="/film-page/:mediaType/:filmId/cast-and-crew-list"
                  element={<CastAndCrewPage />}
                />
                <Route path="/user/:id" element={<ComparisonProfileAccess user={user} />} />
                <Route path="/person/:personId" element={<Person />} />
              </Route>
            )}
            <Route
              path="*"
              element={user ? <Navigate to={`/${user.uid}/profile`} replace /> : <Login />}
            />
          </Routes>
        </Suspense>
      </LanguageBoundary>
    </div>
  );
};

export default AppRouter;
